import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Alert, Snackbar, CircularProgress, Box, Typography, Button } from '@mui/material';
import { auth, db } from './firebase-config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import SignUp from './components/Signup';
import Login from './components/Login';
import Dashboard from './pages/Dashboard';
import SimpleDashboard from './pages/SimpleDashboard';
import ResetPassword from './components/ResetPassword';
import OnboardingFlow from './pages/Onboarding/OnboardingFlow';
import LoadingScreen from './components/LoadingScreen';
import ProtectedRoute from './components/ProtectedRoute';
import PaymentVerificationRoute from './components/PaymentVerificationRoute';
import { checkFirestoreConnection, setOnboardingMode, clearConnectionState } from './utils/firestoreConnectionManager';
import FirestoreRetryPage from './components/FirestoreRetryPage';
import TestAssistant from './components/TestAssistant';
import ManageQA from './components/ManageQA';
import TestInterface from './pages/TestInterface';
import SimpleQAEditor from './pages/SimpleQAEditor';
import NotFound from './pages/NotFound';
import Settings from './components/Settings';
import HelpCenter from './components/HelpCenter';
import Subscription from './pages/Subscription';
import ConversationView from './pages/ConversationView';

// Define theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

function App() {
  // All hooks at the top level
  const [loading, setLoading] = useState(true);
  const [firestoreError, setFirestoreError] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [onboardingComplete, setOnboardingComplete] = useState(null);
  const [isConnectionTesting, setIsConnectionTesting] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [globalError, setGlobalError] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Check for payment_complete parameter on initial load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_complete') === 'true') {
      console.log('Payment complete parameter detected');
      const sessionId = localStorage.getItem('stripeSessionId');

      if (sessionId) {
        console.log('Found Stripe session ID in localStorage:', sessionId);
        // Redirect to signup with session ID
        navigate('/signup', { state: { fromPayment: true, sessionId } });
      } else {
        console.warn('No session ID found in localStorage');
      }
    }
  }, [navigate]);

  // Check if we should load the QA editor directly
  const inQAEditor = localStorage.getItem('in_qa_editor') === 'true';

  // Check onboarding status
  const checkOnboardingStatus = useCallback(async (userId) => {
    if (!userId || inQAEditor) {
      console.log('Skipping onboarding check - QA editor requested directly or no user');
      setOnboardingComplete(false);
      setLoading(false);
      return;
    }

    try {
      const profileRef = doc(db, 'businesses', userId);
      const profileSnapshot = await getDoc(profileRef);

      if (profileSnapshot.exists()) {
        console.log('User profile found in Firestore');
        setUserProfile(profileSnapshot.data());

        // Check if onboarding is complete - prioritize direct flag
        const businessData = profileSnapshot.data();
        const hasOnboardingFlag = businessData.onboardingComplete === true;

        // Fallback to checking required fields if no direct flag
        const hasRequiredFields = businessData &&
                          businessData.businessInfo &&
                          businessData.businessInfo.name &&
                          businessData.phoneData &&
                          businessData.phoneData.phoneNumber &&
                          businessData.introMessage &&
                          businessData.commonQuestions &&
                          businessData.commonQuestions.length > 0;

        const isComplete = hasOnboardingFlag || hasRequiredFields;

        console.log('Onboarding completion check:', {
          hasOnboardingFlag,
          hasRequiredFields,
          isComplete
        });

        setOnboardingComplete(isComplete);
      } else {
        console.log('No profile found, user needs onboarding');
        setOnboardingComplete(false);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      setFirestoreError(true);
      // For connection errors, we still set onboardingComplete to false
      // so the user can still access the app in a limited way
      setOnboardingComplete(false);
    } finally {
      setLoading(false);
    }
  }, [inQAEditor]);

  // Check Firestore connection
  const testFirestoreConnection = useCallback(async () => {
    if (!currentUser) {
      setFirestoreError(false);
      return;
    }

    setIsConnectionTesting(true);

    try {
      const isConnected = await checkFirestoreConnection(currentUser.uid);
      setFirestoreError(!isConnected);
      console.log('Firestore connection test result:', isConnected);
    } catch (error) {
      console.error('Error during connection test:', error);
      setFirestoreError(true);
    } finally {
      setIsConnectionTesting(false);
    }
  }, [currentUser]);

  // Effect for auth state changes
  useEffect(() => {
    console.log('Setting up auth state listener in App component');

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log(`Auth state changed in App. User ${user ? 'logged in' : 'logged out'}`);

      // Handle auth state
      if (user) {
        console.log('User is authenticated:', user.uid);

        // Extract current route info
        const currentPath = window.location.pathname;
        const isOnboardingRoute = currentPath.includes('/onboarding');

        if (isOnboardingRoute) {
          console.log('User is on onboarding route, will attempt to render OnboardingFlow');
        }

        // First, check if user needs onboarding
        try {
          const userRef = doc(db, 'businesses', user.uid);
          const userSnap = await getDoc(userRef);

          if (!userSnap.exists() ||
              (!userSnap.data().businessInfo && !userSnap.data().onboardingComplete)) {
            console.log('No business profile found, user needs onboarding');

            // Set onboarding mode in Firestore to avoid permission errors
            try {
              console.log('Setting onboarding mode for new user');
              await setOnboardingMode(true);

              // Also clear any previous connection state
              try {
                await clearConnectionState(user.uid);
              } catch (e) {
                console.warn('Failed to clear connection state, but continuing:', e);
              }
            } catch (error) {
              console.error('Failed to set onboarding mode in App component:', error);
            }

            // If we're not already on the onboarding page, navigate there
            if (!isOnboardingRoute && currentPath !== '/login' && currentPath !== '/signup') {
              console.log('Redirecting to onboarding...');
              navigate('/onboarding');
            } else {
              console.log('Already on onboarding page OR on login/signup, skipping redirect');
            }

            // Set loading to false here so we don't get stuck in loading screen
            setLoading(false);
            setOnboardingComplete(false);

            return;
          }

          // Check if onboarding is already completed
          if (userSnap.exists() && userSnap.data().onboardingComplete) {
            console.log('User has completed onboarding, updating state');
            setOnboardingComplete(true);

            // If on onboarding route but already completed, redirect to dashboard
            if (isOnboardingRoute) {
              console.log('User completed onboarding but on onboarding route, redirecting to dashboard');
              navigate('/dashboard');
              return;
            }
          }

          // For existing users not on onboarding, test connection normally
          try {
            await testFirestoreConnection();
          } catch (error) {
            console.error('Error during Firestore connection test:', error);
            // Just log the error and continue
            setFirestoreError(true);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error checking user profile:', error);
          setFirestoreError(true);
          setLoading(false);
        }

        // Check onboarding status
        await checkOnboardingStatus(user.uid);
      } else {
        console.log('No user is signed in.');
        setLoading(false);
        setOnboardingComplete(false);
        setFirestoreError(false);
      }
    });

    return () => unsubscribe();
  }, [testFirestoreConnection, navigate, checkOnboardingStatus]);

  // Prepare any UI elements that depend on state
  const offlineWarning = firestoreError ? (
    <Alert severity="warning" sx={{ position: 'fixed', top: 0, width: '100%', zIndex: 9999 }}>
      Database connection issue. Some features may be limited.
      <Button
        color="inherit"
        size="small"
        onClick={() => navigate('/fix-connection')}
      >
        Fix Connection
      </Button>
    </Alert>
  ) : null;

  // Loading screen while app initializes
  if (loading && currentUser) {
    // Check if we're on the onboarding page - if so, don't show loading screen
    const currentPath = window.location.pathname;
    const isOnboardingRoute = currentPath.includes('/onboarding');

    if (isOnboardingRoute) {
      console.log('On onboarding route, skipping loading screen');
      // Don't show loading screen for onboarding route
    } else {
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LoadingScreen message="Loading your profile..." />
        </ThemeProvider>
      );
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {offlineWarning}

      {globalError && (
        <Snackbar
          open={!!globalError}
          autoHideDuration={6000}
          onClose={() => setGlobalError(null)}
        >
          <Alert severity="error" onClose={() => setGlobalError(null)}>
            {globalError}
          </Alert>
        </Snackbar>
      )}

      <Routes>
        <Route path="/signup" element={<SignUp />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/fix-connection" element={<FirestoreRetryPage />} />
        <Route path="/test" element={<TestInterface />} />
        <Route path="/qa-editor" element={<SimpleQAEditor />} />

        <Route path="/onboarding" element={
          <ProtectedRoute>
            <PaymentVerificationRoute>
              {console.log('Rendering OnboardingFlow from App.jsx')}
              <OnboardingFlow />
            </PaymentVerificationRoute>
          </ProtectedRoute>
        } />

        <Route path="/test-assistant" element={
          <ProtectedRoute>
            {currentUser ? (
              <TestAssistant />
            ) : (
              <Navigate to="/login" replace />
            )}
          </ProtectedRoute>
        } />

        <Route path="/manage-qa" element={
          <ProtectedRoute>
            {currentUser ? (
              <ManageQA />
            ) : (
              <Navigate to="/login" replace />
            )}
          </ProtectedRoute>
        } />

        <Route path="/settings" element={
          <ProtectedRoute>
            {currentUser ? (
              <Settings />
            ) : (
              <Navigate to="/login" replace />
            )}
          </ProtectedRoute>
        } />

        <Route path="/help" element={
          <ProtectedRoute>
            {currentUser ? (
              <HelpCenter />
            ) : (
              <Navigate to="/login" replace />
            )}
          </ProtectedRoute>
        } />

        <Route path="/subscription/:businessId" element={
          <ProtectedRoute>
            {currentUser ? (
              <Subscription />
            ) : (
              <Navigate to="/login" replace />
            )}
          </ProtectedRoute>
        } />

        <Route path="/subscription" element={
          <ProtectedRoute>
            {currentUser ? (
              <Subscription />
            ) : (
              <Navigate to="/login" replace />
            )}
          </ProtectedRoute>
        } />

        <Route path="/conversation/:id" element={
          <ProtectedRoute>
            {currentUser ? (
              <ConversationView />
            ) : (
              <Navigate to="/login" replace />
            )}
          </ProtectedRoute>
        } />

        <Route path="/dashboard/*" element={
          <ProtectedRoute>
            {currentUser ? (
              <>
                {console.log("Rendering Dashboard, onboardingComplete:", onboardingComplete)}
                <Dashboard />
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
              </Box>
            )}
          </ProtectedRoute>
        } />

        <Route path="/debug-dashboard" element={
          <ProtectedRoute>
            {currentUser ? (
              <>
                {console.log("Rendering SimpleDashboard, onboardingComplete:", onboardingComplete)}
                <SimpleDashboard />
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
              </Box>
            )}
          </ProtectedRoute>
        } />

        <Route path="/" element={
          currentUser ? (
            <>
              {console.log("Root route: currentUser exists, onboardingComplete =", onboardingComplete)}
              {onboardingComplete ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/onboarding" replace />
              )}
            </>
          ) : (
            <Navigate to="/login" replace />
          )
        } />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </ThemeProvider>
  );
}

export default App;